// src/pages/auth/SignupPage.tsx
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import { mapSignupError } from '../../lib/authErrors.ts';
import { Link, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuthActions, useAuthState } from '../../auth/AuthProvider';
import { useRouteTitle } from '../../hooks/useRouteTitle';
import BrandWordmark from '../../components/brand/BrandWordmark';
import { httpsCallable } from 'firebase/functions';
import { signInWithCustomToken, signInWithEmailAndPassword } from 'firebase/auth';
import { auth, functions } from '@/firebase';
import { useSubscription } from '@/hooks/useSubscription';
import SubscriptionEnforcementCard from '@/components/auth/SubscriptionEnforcementCard';
import toast from 'react-hot-toast';

/**
 * SignupPage - SRL Claim-Based Account Creation
 * 
 * IMPORTANT: This is NOT a role selection page. Marketing site (threadlock.ai/signup)
 * handles SRL vs Pro selection. This page is ONLY for completing SRL account creation
 * after successful Stripe checkout.
 * 
 * State A: No Claim (routing recovery)
 * - User landed here without coming from Stripe success
 * - Show neutral "Start with billing" CTA → /subscribe
 * - This is NOT an error, just a routing recovery screen
 * 
 * State B: Claim Present
 * - User completed Stripe checkout, has claim token
 * - Show password/profile form
 * - Call claimAccount function → sign in → redirect to /verify-email
 * 
 * State C: Already Authenticated
 * - If active subscription + verified email → redirect to /cases
 * - If no active subscription → redirect to /subscribe (or show enforcement)
 * - If unverified email → redirect to /verify-email
 */
export default function SignupPage() {
  useRouteTitle("Sign Up");
  const { signUp, signOutUser } = useAuthActions();
  const { user, initializing } = useAuthState();
  const { status: subscriptionStatus, loading: subscriptionLoading } = useSubscription();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const claimToken = searchParams.get('claim');
  
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const checkingAuth = initializing;

  // Redirect authenticated users with active subscription to dashboard
  useEffect(() => {
    if (!initializing && user && !subscriptionLoading && subscriptionStatus === 'active') {
      console.info('[SignupPage] User already authenticated with active subscription, redirecting to dashboard');
      navigate('/cases', { replace: true });
    }
  }, [user, initializing, subscriptionStatus, subscriptionLoading, navigate]);

  // Show loading while checking auth state
  if (checkingAuth) {
    return <GateMessage>Checking authorization...</GateMessage>;
  }

  // ROUTING LOGIC:
  // State B: Claim present → Show account creation form
  if (claimToken) {
    return <ClaimSignupForm 
      claimToken={claimToken}
      email={email}
      setEmail={setEmail}
      fullName={fullName}
      setFullName={setFullName}
      pw={pw}
      setPw={setPw}
      pw2={pw2}
      setPw2={setPw2}
      showPw={showPw}
      setShowPw={setShowPw}
      showPw2={showPw2}
      setShowPw2={setShowPw2}
      tosAccepted={tosAccepted}
      setTosAccepted={setTosAccepted}
      err={err}
      setErr={setErr}
      submitting={submitting}
      setSubmitting={setSubmitting}
      navigate={navigate}
    />;
  }

  // State C: Authenticated but no active subscription → Show enforcement card
  if (user && !subscriptionLoading && subscriptionStatus !== 'active') {
    return <SubscriptionEnforcementCard />;
  }

  // State A: No claim (routing recovery)
  // User landed here without coming from Stripe success
  // Show neutral billing CTA - this is NOT an error
  return <NoClaimRecoveryScreen />;
}

/**
 * ClaimSignupForm - Account Creation After Payment
 * 
 * This form is shown only when a claim token is present (Mode 2)
 * Purpose: Complete account setup after successful Stripe checkout
 */
interface ClaimSignupFormProps {
  claimToken: string;
  email: string;
  setEmail: (email: string) => void;
  fullName: string;
  setFullName: (name: string) => void;
  pw: string;
  setPw: (pw: string) => void;
  pw2: string;
  setPw2: (pw2: string) => void;
  showPw: boolean;
  setShowPw: React.Dispatch<React.SetStateAction<boolean>>;
  showPw2: boolean;
  setShowPw2: React.Dispatch<React.SetStateAction<boolean>>;
  tosAccepted: boolean;
  setTosAccepted: (accepted: boolean) => void;
  err: string | null;
  setErr: (err: string | null) => void;
  submitting: boolean;
  setSubmitting: (submitting: boolean) => void;
  navigate: (path: string, options?: { replace?: boolean }) => void;
}

function ClaimSignupForm({
  claimToken,
  email,
  setEmail,
  fullName,
  setFullName,
  pw,
  setPw,
  pw2,
  setPw2,
  showPw,
  setShowPw,
  showPw2,
  setShowPw2,
  tosAccepted,
  setTosAccepted,
  err,
  setErr,
  submitting,
  setSubmitting,
  navigate
}: ClaimSignupFormProps) {
  
  
  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;

    setErr(null);
    if (!fullName.trim()) return setErr('Full name is required for conflict checks.');
    if (!tosAccepted) return setErr('You must agree to the Terms of Service and Privacy Policy.');
    if (pw.length < 8) return setErr('Password must be at least 8 characters.');
    if (pw !== pw2) return setErr('Passwords do not match.');
    if (!claimToken) return setErr('Invalid signup session. Please start from /subscribe');
    
    setSubmitting(true);
    try {
      // P0: Call server to create account via claim
      // Server validates claim, creates Firebase Auth user, syncs billing
      const claimAccountFn = httpsCallable<
        { claimToken: string; password: string; displayName: string },
        { uid: string; email: string; plan: string; customToken: string }
      >(functions, 'claimAccount');

      const result = await claimAccountFn({
        claimToken,
        password: pw,
        displayName: fullName.trim()
      });

      console.info('[SignupPage] Account created via claim:', result.data.uid);
      console.info('='.repeat(80));
      console.info('ACCOUNT CREATED SUCCESSFULLY');
      console.info('UID:', result.data.uid);
      console.info('Email:', result.data.email);
      console.info('Plan:', result.data.plan);
      console.info('='.repeat(80));

      // P4: Sign in with custom token if available, else fall back to email/password.
      // createCustomToken can fail in production if the service account lacks
      // roles/iam.serviceAccountTokenCreator — the account is still created successfully.
      let userCredential;
      if (result.data.customToken) {
        userCredential = await signInWithCustomToken(auth, result.data.customToken);
      } else {
        console.info('[SignupPage] customToken unavailable — falling back to email/password sign-in');
        userCredential = await signInWithEmailAndPassword(auth, result.data.email, pw);
      }
      const currentUser = userCredential.user;
      
      // Wait for Firebase Auth session to fully initialize (prevents email delivery delays)
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // P4: Claim-based accounts (Lane B) have payment as identity proof - skip email verification
      console.info('[SignupPage] Current user after sign-in:', {
        exists: !!currentUser,
        email: currentUser?.email,
        emailVerified: currentUser?.emailVerified,
        uid: currentUser?.uid,
        plan: result.data.plan
      });
      
      // Email already verified for claim-based accounts (set by backend)
      if (currentUser?.emailVerified) {
        console.info('[SignupPage] ✅ Claim-based account - email verification bypassed (payment = identity proof)');
        
        // P4: Verify Firestore user document and subscription were created
        console.info('[SignupPage] Verifying Firestore documents...');
        try {
          const { doc, getDoc, collection } = await import('firebase/firestore');
          const { db } = await import('@/firebase');
          
          // Check user document
          const userRef = doc(db, 'users', currentUser.uid);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            const userData = userSnap.data();
            console.info('='.repeat(80));
            console.info('FIRESTORE USER DOCUMENT:');
            console.info('Role:', userData.role);
            console.info('Plan:', userData.plan);
            console.info('Email:', userData.email);
            console.info('Signup Method:', userData.signupMethod);
            console.info('='.repeat(80));
          } else {
            console.error('[SignupPage] ❌ User document NOT FOUND in Firestore');
          }
          
          // Check subscription document
          const subRef = doc(db, 'users', currentUser.uid, 'subscription', 'current');
          const subSnap = await getDoc(subRef);
          
          if (subSnap.exists()) {
            const subData = subSnap.data();
            console.info('='.repeat(80));
            console.info('SUBSCRIPTION DOCUMENT:');
            console.info('Status:', subData.status);
            console.info('Plan:', subData.plan);
            console.info('Stripe Subscription ID:', subData.stripeSubscriptionId);
            console.info('='.repeat(80));
          } else {
            console.error('[SignupPage] ❌ Subscription document NOT FOUND');
          }
        } catch (firestoreCheckError: any) {
          console.error('[SignupPage] Failed to verify Firestore documents:', firestoreCheckError);
        }
        
        // P4: Wait for billing sync to complete before redirecting
        // This prevents the billing redirect dead end
        console.info('[SignupPage] Waiting for billing sync...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Verify billingCustomers document exists
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('@/firebase');
          const billingRef = doc(db, 'billingCustomers', currentUser.uid);
          const billingSnap = await getDoc(billingRef);
          
          if (!billingSnap.exists()) {
            console.warn('[SignupPage] ⚠️ Billing sync incomplete after 2s - proceeding anyway');
            toast('Setting up your account...', { icon: '⏳' });
          } else {
            console.info('[SignupPage] ✅ Billing sync complete');
          }
        } catch (billingCheckError: any) {
          console.error('[SignupPage] Failed to check billing sync:', billingCheckError);
          // Continue anyway - onboarding will handle it
        }
        
        toast.success('Welcome to ThreadLock! Let\'s create your first case.');
        console.info('[SignupPage] Navigating to /cases/new');
        // Redirect to new-case page — the canonical in-app case creation flow.
        // NewCasePage creates cases with onboardingCompleted:false which triggers
        // the DashboardTab wizard for guided first-use, matching existing UX.
        navigate('/cases/new', { replace: true });
        return;
      }
      
      // This should not happen for claim-based accounts, but handle gracefully
      console.warn('[SignupPage] ⚠️ Claim-based account but emailVerified=false - investigating');
      
      if (currentUser && !currentUser.emailVerified) {
        try {
          // Use centralized email verification service
          const { sendVerificationEmail } = await import('@/lib/emailVerification');
          await sendVerificationEmail({ user: currentUser, isPro: false });
        } catch (emailError: any) {
          console.error('[SignupPage] ❌ Failed to send verification email:', emailError);
          toast.error(`Account created but verification email failed: ${emailError.message}. You can resend from the next page.`);
        }
      } else {
        console.error('[SignupPage] ❌ No current user after sign-in!');
        toast.error('Session error - please try signing in manually');
      }

      // Redirect to email verification page (user is now signed in)
      toast.success('Account created! Please verify your email.');
      console.info('[SignupPage] Navigating to /verify-email');
      navigate('/verify-email', { replace: true });
    } catch (e: any) {
      console.error('[SignupPage] Claim account error:', e);
      const errorMessage = e.message || 'Failed to create account. Please contact support.';
      setErr(errorMessage);
      setSubmitting(false);
    }
  }

  return (
    <div className="relative min-h-screen w-full bg-transparent">
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4">
        
        <div data-testid="logged-out-state" className="sr-only">
          logged-out
        </div>
        
        <div className="relative w-full max-w-md rounded-2xl border border-orange-600/50 bg-slate-900/90 p-8 shadow-2xl backdrop-blur-sm">
        {/* BrandWordmark */}
        <div className="mb-8 flex justify-center">
          <BrandWordmark darkText={false} className="text-3xl" />
        </div>
        
        <h1 className="mb-6 text-center text-2xl font-semibold text-white">Create an Account</h1>
        
        <form onSubmit={onSubmit} className="space-y-4">
          <input
            className="w-full rounded-xl border border-slate-600 bg-slate-700 p-3 text-white placeholder-slate-400 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50"
            type="text"
            name="fullName"
            id="fullName"
            placeholder="Full Name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            autoComplete="name"
          />
          <input
            className="w-full rounded-xl border border-slate-600 bg-slate-700 p-3 text-white placeholder-slate-400 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50"
            type="email"
            name="email"
            id="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
          <div className="relative">
            <input
              className="w-full rounded-xl border border-slate-600 bg-slate-700 p-3 pr-12 text-white placeholder-slate-400 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50"
              type={showPw ? 'text' : 'password'}
              name="password"
              placeholder="Password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              aria-pressed={showPw}
              aria-label={showPw ? 'Hide password' : 'Show password'}
              data-cy="toggle-password-visibility"
              className="absolute inset-y-0 right-2 my-auto flex h-8 items-center justify-center rounded-md px-2 text-slate-400 transition hover:bg-slate-600/50 hover:text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50"
            >
              {showPw ? <EyeOff size={18} aria-hidden /> : <Eye size={18} aria-hidden />}
            </button>
          </div>
          <div className="relative">
            <input
              className="w-full rounded-xl border border-slate-600 bg-slate-700 p-3 pr-12 text-white placeholder-slate-400 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50"
              type={showPw2 ? 'text' : 'password'}
              name="confirm-password"
              placeholder="Confirm password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPw2((v) => !v)}
              aria-pressed={showPw2}
              aria-label={showPw2 ? 'Hide password' : 'Show password'}
              data-cy="toggle-confirm-password-visibility"
              className="absolute inset-y-0 right-2 my-auto flex h-8 items-center justify-center rounded-md px-2 text-slate-400 transition hover:bg-slate-600/50 hover:text-white focus:outline-none focus:ring-2 focus:ring-orange-500/50"
            >
              {showPw2 ? <EyeOff size={18} aria-hidden /> : <Eye size={18} aria-hidden />}
            </button>
          </div>
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="tos-consent"
              checked={tosAccepted}
              onChange={(e) => setTosAccepted(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-700 text-orange-600 focus:ring-2 focus:ring-orange-500/50"
              required
            />
            <label htmlFor="tos-consent" className="text-sm text-slate-300">
              I agree to the{' '}
              <Link to="/terms" className="text-orange-400 underline hover:text-orange-300" target="_blank">
                Terms of Service
              </Link>{' '}
              and acknowledge the{' '}
              <Link to="/privacy" className="text-orange-400 underline hover:text-orange-300" target="_blank">
                Privacy Policy
              </Link>
              .
            </label>
          </div>
          {err ? (
            <p className="rounded-md border border-red-500/60 bg-red-500/10 px-3 py-2 text-sm text-red-400" role="alert">
              {err}
            </p>
          ) : null}
          <button
            disabled={submitting}
            className="flex w-full items-center justify-center rounded-2xl border border-transparent bg-orange-600 py-3 text-base font-semibold text-white transition hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500/60 active:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? 'Creating...' : 'Create account'}
          </button>
        </form>
        
        <p className="mt-6 text-center text-sm text-slate-300">
          Already have an account?{' '}
          <Link className="text-orange-400 underline hover:text-orange-300" to="/login">
            Sign in
          </Link>
        </p>
      </div>
      </div>
    </div>
  );
}

/**
 * NoClaimRecoveryScreen - Routing Recovery (State A)
 * 
 * Shown when user lands on /signup without a claim token.
 * This happens when someone bookmarks or directly navigates to /signup
 * without going through Stripe checkout first.
 * 
 * Purpose: Guide them to billing (NOT an error screen)
 * Tone: Neutral and helpful, not scolding
 */
function NoClaimRecoveryScreen() {
  return (
    <div className="relative min-h-screen w-full bg-transparent">
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4">
        <div className="relative w-full max-w-md rounded-2xl border border-orange-600/50 bg-slate-900/90 p-8 backdrop-blur-sm shadow-2xl">
          <div className="mb-8 flex justify-center">
            <BrandWordmark darkText={false} className="text-3xl" />
          </div>
          
          <h1 className="mb-4 text-center text-2xl font-semibold text-white">
            Complete Your Signup
          </h1>
          
          <p className="mb-6 text-center text-sm text-white/80">
            To create an account, start with billing so we can verify your subscription.
          </p>
          
          <Link
            to="/subscribe"
            className="block w-full rounded-2xl border border-transparent bg-orange-600 py-3 text-center text-base font-semibold text-white transition hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500/60"
          >
            Continue to Billing
          </Link>
          
          <div className="mt-6 border-t border-slate-700 pt-6 text-center">
            <p className="text-sm text-white/60 mb-2">Already completed checkout?</p>
            <p className="text-xs text-white/50">Check your email for the signup link with your claim code.</p>
          </div>
          
          <div className="mt-4 text-center">
            <div className="flex gap-4 justify-center text-sm">
              <Link to="/terms" className="text-orange-400 underline hover:text-orange-300">
                Terms
              </Link>
              <Link to="/privacy" className="text-orange-400 underline hover:text-orange-300">
                Privacy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GateMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-transparent px-4">
      <div className="relative w-full max-w-md rounded-2xl border border-orange-600/50 bg-slate-900/90 p-6 text-center backdrop-blur-sm shadow-2xl">
        <div className="mb-6 flex justify-center">
          <BrandWordmark darkText={false} className="text-2xl" />
        </div>
        <div className="text-sm text-slate-300">
          {children}
        </div>
      </div>
    </div>
  );
}